import { useState } from 'react';
import { SITE_URL } from '@/constants/site';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: 'What is the VigorBuy Spreadsheet?',
    answer: 'The VigorBuy Spreadsheet is a regularly updated product resource that organizes verified product listings into easy-to-navigate categories. It includes shoes, hoodies, t-shirts, jackets, accessories, and more, with direct links to purchase each item from trusted sources.',
  },
  {
    question: 'How often is the VigorBuy Spreadsheet updated?',
    answer: 'The VigorBuy Spreadsheet is updated daily with new products, pricing changes, and availability information. Each update is verified by the team to ensure accuracy and reliability for all users.',
  },
  {
    question: 'Is the VigorBuy Spreadsheet free to use?',
    answer: 'Yes, the VigorBuy Spreadsheet is completely free to access and use. There are no subscription fees or hidden charges. Simply browse the categories and find the products you are looking for.',
  },
  {
    question: 'How do I use the VigorBuy Spreadsheet to find products?',
    answer: 'Using the VigorBuy Spreadsheet is straightforward. Open the spreadsheet, navigate to the category you are interested in, browse the organized product listings, compare options, and use the provided links to make your purchase on the respective platforms.',
  },
  {
    question: 'Is the VigorBuy Spreadsheet safe and legitimate?',
    answer: 'Yes, the VigorBuy Spreadsheet is a legitimate and safe resource. Every product listing is manually verified before being added. The spreadsheet does not collect personal data, and purchases are made on external, secure platforms, giving you full control over your transactions.',
  },
  {
    question: 'What product categories are available in the VigorBuy Spreadsheet?',
    answer: 'The VigorBuy Spreadsheet covers 14 categories including Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear/Underpants, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category contains verified listings with direct purchase links.',
  },
  {
    question: 'Can I get shipping guidance from the VigorBuy Spreadsheet?',
    answer: 'Yes, the VigorBuy Spreadsheet includes shipping guidance and tutorials to help you understand shipping options, estimated delivery times, and best practices for international orders. This ensures a smooth buying experience from start to finish.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      {/* FAQPage Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />

      <section id="safety" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 text-xs font-medium text-secondary-600 bg-secondary-50 rounded-full mb-4">
              FAQ
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
              Frequently Asked Questions About the VigorBuy Spreadsheet
            </h2>
          </div>

          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                >
                  <h4 className="font-heading text-base md:text-lg font-semibold text-foreground-900 pr-4">
                    {item.question}
                  </h4>
                  <div className={`flex-shrink-0 w-6 h-6 flex items-center justify-center transition-transform duration-300 ${openIndex === index ? 'rotate-45' : ''}`}>
                    <i className="ri-add-line text-foreground-500"></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-6 pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}